/**
 * UnifiedSearchBar - Global search across all modules
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../../App";
import axios from "axios";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Search, FileText, User, Building2, AlertTriangle, Gavel,
  Mail, MessageSquare, Loader2, X
} from "lucide-react";

const TYPE_ICONS = {
  document: <FileText className="w-4 h-4 text-blue-500" />,
  owner: <User className="w-4 h-4 text-green-500" />,
  property: <Building2 className="w-4 h-4 text-amber-500" />,
  violation: <AlertTriangle className="w-4 h-4 text-red-500" />,
  complaint: <Gavel className="w-4 h-4 text-purple-500" />,
  correspondence: <Mail className="w-4 h-4 text-indigo-500" />,
  forum: <MessageSquare className="w-4 h-4 text-teal-500" />,
};

const UnifiedSearchBar = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (value.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search?q=${encodeURIComponent(value)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (result) => {
    setOpen(false);
    setQuery("");
    navigate(result.url);
  };

  return (
    <div ref={ref} className="relative w-full max-w-md" data-testid="unified-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search everything..."
          className="pl-10 pr-10 h-9 bg-muted/50 border-none"
          data-testid="search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
        {query && !loading && (
          <button onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-card border rounded-lg shadow-lg z-50 max-h-80 overflow-auto"
          data-testid="search-results">
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}-${i}`}
              onClick={() => handleSelect(r)}
              className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-muted/50 text-left border-b last:border-0"
              data-testid={`search-result-${i}`}
            >
              {TYPE_ICONS[r.type] || <Search className="w-4 h-4" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.title}</p>
                {r.subtitle && <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>}
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{r.type}</Badge>
            </button>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-card border rounded-lg shadow-lg z-50 p-4 text-center text-sm text-muted-foreground">
          No results found for "{query}"
        </div>
      )}
    </div>
  );
};

export default UnifiedSearchBar;
